import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, apiTokenRequest, loginRequest } from './msal';
import { localToken } from './localMode';

// ── Mock-auth bypass ──────────────────────────────────────────────────────────
//
// When VITE_MOCK_AUTH=true (local dev — no real Azure AD registration), MSAL
// has no active account and every acquireToken* call throws.  In that mode we
// skip the token entirely and rely on the API's matching dev bypass
// (STRIDE_AUTH_DEV_BYPASS) — the API DOES validate bearer tokens now, and
// refuses every /api request without one unless that bypass is on,
// matching the behaviour of HttpConnectionService's plain fetch helper.
//
// When VITE_MOCK_AUTH is absent / false, the full MSAL silent→popup flow runs
// exactly as before.
//
// NOTE: read at call-time (not module-init) so that:
//  a) vi.stubEnv() in tests overrides it correctly, and
//  b) .env.local changes take effect without a build restart.

function isMockAuth(): boolean {
  return import.meta.env.VITE_MOCK_AUTH === 'true';
}

/**
 * One re-auth per tab, ever.
 *
 * A 401 used to send the browser to sign-in unconditionally. When the user IS signed in and a single
 * endpoint rejects their token, that is a LOOP: redirect out, Entra still has a session so it redirects
 * straight back, the page loads, the same call 401s, round again — five or six times until Entra
 * throttles and drops them on the sign-in screen. One failing endpoint becomes a total lockout, and the
 * symptom ("it keeps refreshing") names neither the call nor the cause.
 *
 * Renewing an expired session is worth exactly one attempt: if signing in again did not fix it, the
 * problem was never the session, and trying harder cannot help. After that the 401 is surfaced —
 * naming the route — so the next person sees which call is failing instead of a spinning page.
 *
 * Session-scoped rather than module-scoped so a genuine expiry in a later tab still gets its one try.
 */
const REAUTH_KEY = 'stride.reauth-attempted';

/**
 * The mark has to survive a page load, because the redirect IS a page load — a module-scoped flag resets
 * on the way back and bounds nothing. So it lives in sessionStorage.
 *
 * And if sessionStorage cannot be written, we do NOT redirect at all. A redirect we cannot remember
 * making is precisely the loop this exists to stop, so "cannot record it" has to mean "do not do it".
 * Surfacing the 401 is a worse experience than a successful renewal and a far better one than being
 * bounced through Entra until it throttles.
 */
function canRecordReauth(): boolean {
  try {
    const probe = `${REAUTH_KEY}.probe`;
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return true;
  } catch { return false; }
}
function reauthAlreadyTried(): boolean {
  try { return window.sessionStorage.getItem(REAUTH_KEY) === '1'; } catch { return true; }
}
function markReauthTried(): void {
  try { window.sessionStorage.setItem(REAUTH_KEY, '1'); } catch { /* checked by canRecordReauth first */ }
}

// ── Active git connection ─────────────────────────────────────────────────────
//
// The active git connection (chosen via the sidebar repo switcher) is threaded
// into every /api/git/* request as ?connectionId=<id>.  The backend uses it to
// pick which stored GitHub credential to authenticate with.  We hold it in a
// module-level variable (set by AppContext when the active connection changes)
// rather than passing it through every git-service call site.

let _activeGitConnectionId: string | null = null

/** Set (or clear) the connectionId appended to /api/git/* requests. */
export function setActiveGitConnectionId(id: string | null): void {
  _activeGitConnectionId = id
}

// ── Error type ────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    message: string,
    /**
     * The parsed error body, when the response carried JSON.
     *
     * Some 4xx responses are ANSWERS rather than failures — a 409 from the story-scope
     * write returns the scope that is currently in Jira so the UI can show the
     * difference. Discarding the body would force a second round trip to learn what
     * the server already said.
     */
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Token acquisition ─────────────────────────────────────────────────────────

/**
 * Returns an access token for the currently authenticated user.
 *
 * Three modes, checked in this order:
 *   1. Desktop app — the per-launch loopback token from the Electron bridge.
 *   2. Mock auth (`VITE_MOCK_AUTH=true`) — `null`, so the caller omits the header entirely and the
 *      API's dev bypass decides.
 *   3. Otherwise the full MSAL silent → popup flow.
 *
 * The desktop check comes FIRST, deliberately. It is a property of the host, not of how the bundle
 * was built, so it must win over whatever `VITE_MOCK_AUTH` happened to be set to when these assets
 * were compiled — the desktop app ships the same build as the hosted instance, and sending no token
 * there would 401 every request.
 *
 * This is also the single seam the Socket.IO factory uses (`lib/socket.ts`), so authenticating the
 * REST surface and the realtime one cannot drift apart.
 */
export async function getAccessToken(): Promise<string | null> {
  const local = localToken();
  if (local !== null) return local;

  if (isMockAuth()) return null;

  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0];

  if (!account) {
    throw new ApiError(401, 'Unauthorized', 'No active account — user must log in.');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const result = await msalInstance.acquireTokenPopup({ ...apiTokenRequest, account });
      return result.accessToken;
    }
    throw err;
  }
}

/**
 * The Authorization header for an /api call, or `{}` in mock-auth dev mode.
 *
 * Several services predate this module and grew their own `apiFetch` helper with their own error
 * semantics (one parses JSON messages, one returns raw text, one treats a 409 as a valid answer).
 * Rewriting them onto `request()` would silently change those contracts, so they keep their helper
 * and spread THIS in. One place decides how a token is obtained; nobody hand-builds the header.
 *
 * Anything talking to /api must use this or `request()` — a bare `fetch` is refused by the API the
 * moment auth is real, which is a failure that never shows up while the dev bypass is on.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return {
    ...(token !== null ? { Authorization: `Bearer ${token}` } : {}),
    ...projectHeader(),
  };
}

// ── Active project ────────────────────────────────────────────────────────────
//
// Which project the UI is acting in, sent on EVERY request as `X-Stride-Project`.
//
// This is not a convenience. The API resolves the project from this header and scopes every read to
// it — credentials, pipelines, sets, runs. A request that omits it falls back to the caller's default
// project, so a switcher that changed a label without changing the header would show one project's
// name above another project's data. That is the worst possible failure for a tenancy feature: it
// looks like it works.
//
// Held in a module variable and mirrored to localStorage rather than read from React state, because
// the services that call the API are not all inside the component tree.

const PROJECT_STORAGE_KEY = 'stride.activeProject'

let _activeProject: string | null = null

/** Restore the last-used project on boot, so a reload does not silently switch context. */
export function initActiveProject(): string | null {
  try { _activeProject = window.localStorage.getItem(PROJECT_STORAGE_KEY); } catch { /* private mode */ }
  return _activeProject;
}

export function setActiveProject(id: string | null): void {
  _activeProject = id;
  try {
    if (id === null) window.localStorage.removeItem(PROJECT_STORAGE_KEY);
    else window.localStorage.setItem(PROJECT_STORAGE_KEY, id);
  } catch { /* private mode — the header still works for this session */ }
}

export function getActiveProject(): string | null {
  return _activeProject;
}

function projectHeader(): Record<string, string> {
  // Omitted rather than sent empty when unset: the API treats an absent header as "use my default",
  // and an empty one would be a project named "".
  return _activeProject !== null && _activeProject.length > 0
    ? { 'X-Stride-Project': _activeProject }
    : {};
}

// ── Internal request helper ───────────────────────────────────────────────────

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getAccessToken();

  // Thread the active git connectionId into git-scoped requests.  Only touch
  // /api/git/* paths, only when a connection is active, and never clobber a
  // connectionId the caller already specified.
  let resolvedPath = path;
  if (
    _activeGitConnectionId !== null &&
    path.startsWith('/api/git/') &&
    !path.includes('connectionId=')
  ) {
    const separator = path.includes('?') ? '&' : '?';
    resolvedPath = `${path}${separator}connectionId=${encodeURIComponent(_activeGitConnectionId)}`;
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Omit Authorization when there is no token (mock-auth dev mode)
    ...(token !== null ? { Authorization: `Bearer ${token}` } : {}),
    // Every request says which project it acts in. See the note above projectHeader().
    ...projectHeader(),
  };

  const init: RequestInit = {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  const resp = await fetch(resolvedPath, init);

  // A successful call proves the session works, so a LATER expiry gets its own attempt.
  // Deliberately NOT cleared here.
  //
  // This used to clear the re-auth bound on ANY successful response, so a session expiring an hour
  // later would still renew silently. In production that defeated the bound entirely: `/api/me` and
  // `/api/connections` answered 200 immediately before the one call that 401'd, wiping the mark every
  // cycle, so the redirect fired again and again - about 120 times in 25 seconds.
  //
  // One automatic re-authentication per tab, full stop. A genuine later expiry now gets the message
  // below, which names the failing call, instead of a silent bounce through Entra. A bound that any
  // unrelated success can erase is not a bound.

  if (resp.status === 401) {
    // Under mock auth there is no session to renew, so a redirect can only be a dead end: it navigates
    // away from the app, comes back unauthenticated, and the next call 401s again. Worse, it hides WHICH
    // call failed — one expired connection credential made the whole local app look like a login problem,
    // and cost an afternoon to trace back to a single 401 on /api/arm-data/identity-drift.
    //
    // A 401 in this mode means the API rejected the request on its own terms (its dev bypass is off, or a
    // stored credential the route needs has expired). Surface that instead of bouncing.
    if (isMockAuth()) {
      throw new ApiError(
        401, 'Unauthorized',
        `The API rejected ${method} ${resolvedPath} (401). Mock auth is on, so this is not a sign-in problem: `
        + 'either STRIDE_AUTH_DEV_BYPASS is off in the API, or a stored credential this route needs has expired.',
      );
    }
    if (!canRecordReauth() || reauthAlreadyTried()) {
      // Signing in again did not fix it, so the session was never the problem. Say which call is
      // failing rather than bouncing the user through Entra until it throttles them out.
      throw new ApiError(
        401, 'Unauthorized',
        `Signed in, but the API rejected ${method} ${resolvedPath} (401). Re-authenticating did not help `
        + '(or could not be tracked), so this is not an expired session — the token is being refused for '
        + 'this request.',
      );
    }
    markReauthTried();
    await msalInstance.loginRedirect(loginRequest);
    throw new ApiError(401, 'Unauthorized', 'Session expired — signing in again.');
  }

  if (!resp.ok) {
    let message = resp.statusText;
    let parsed: unknown;
    try {
      parsed = await resp.json();
      const errBody = parsed as { message?: string; error?: string };
      message = errBody.message ?? errBody.error ?? message;
    } catch {
      // ignore JSON parse failures — use statusText
    }
    throw new ApiError(resp.status, resp.statusText, message, parsed);
  }

  if (resp.status === 204) {
    return undefined as unknown as T;
  }

  return resp.json() as Promise<T>;
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function apiGet<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body);
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, body);
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, body);
}

export async function apiDelete(path: string): Promise<void> {
  await request<void>('DELETE', path);
}
